import axios from 'axios';
import { dedupeMarkers, filterOldShipDates, cleanLeads, displaceDuplicates } from '../utils/dedupeMarkers';

let configPromise = fetch(`/env${import.meta.env.VITE_ENVIRONMENT || ""}.json`).then((response) => response.json());
if (import.meta.env.VITE_ENVIRONMENT == "dev") {
  console.log('Picking dev creds');
  configPromise = new Promise((resolve, reject) => resolve({ PROTOCOL: import.meta.env.VITE_PROTOCOL, HOST: import.meta.env.VITE_HOST }))
}
export const apiPromise = configPromise.then((config) => axios.create({
  baseURL: `${config.PROTOCOL}://${config.HOST}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
}));

let cachedLocations = [];

// ---- Global 401/403 trap: dispatch a browser event once --------
let sessionFiredOnce = false;
const GUARDED = [
  /\/v1\/locations\/search\b/i,
  /\/v1\/locations\/account\b/i,
  /\/v1\/locations\/leads\b/i,
];

apiPromise.then((api) => {
  api.interceptors.response.use(
    (res) => res, // pass successful responses through
    (error) => {
      const status = error?.response?.status;
      const errorMsg = error?.response?.data?.error || '';

      // Check for 500 error and specific 'Missing session attribute' message
      if (status === 500 && errorMsg.includes("Missing session attribute 'oauth_Token'")) {
        console.log("Session token missing! Triggering session expired.");
        window.dispatchEvent(new CustomEvent('sysco:session-expired'));
      }

      return Promise.reject(error);
    }
  );
});

export async function searchLocations(params) {
  try {
    console.log('calling locations api with params:', params);

    const api = await apiPromise;

    // 1. Call main search API
    const mainResponse = await api.get('/v1/locations/search', { params });
    console.log("Main Locations: ", mainResponse);

    if (params.query) {
      console.log("Saving search for query:", params.query);
      saveSearch('Location', params.query);
    }
    // 2. Call leads API (only if location param is present)
    let leadsResponse = { data: { locations: [] } };
    try {
      if (params.lat && params.lng) {
        const locationStr = `${params.lat},${params.lng}`;
        leadsResponse = await api.get('/v1/locations/leads', {
          params: {
            location: locationStr,
            radius: params.radius || 5,
            type: 'Sysco360',
          },
        });
        console.log("leads: ", leadsResponse.data)
      }
      else if (params.query) {
        leadsResponse = await api.get('/v1/locations/leads', {
          params: {
            query: params.query,
            radius: mainResponse.data.data.metadata.searchRadius || 5,
            type: 'Sysco360',
          },
        });
      }
    }
    catch (error) {
      console.error('Leads API error:', error);
    }

    console.log("Lead Locations: ", leadsResponse.data.data.locations);

    const mainData = mainResponse.data
    const leadsData = leadsResponse.data

    if (leadsData?.data?.locations) {
      leadsData.data.locations = cleanLeads(leadsData.data.locations);
    }



    // Limit lead targets to 20 TEMPORARY
    // if (leadsData?.data?.locations) {
    //   leadsData.data.locations = leadsData.data.locations.slice(0, 20);
    // }


    // 3. Merge both result sets
    console.log("Lead Locations: ", leadsData.data);
    let mergedLocations = [
      ...(mainData?.data?.locations || []),
      ...(leadsData?.data?.locations || [])
    ];
    console.log("Total Locations: ", mergedLocations);
    mergedLocations = dedupeMarkers(mergedLocations, { debug: false });
    console.log("Total Locations: ", mergedLocations);
    mergedLocations = mergedLocations.filter(Boolean);

    console.log("Total number Locations: ", mergedLocations.length);
    const counts = {
      ACTIVE: 0,
      ACTIVEOLDER: 0,
      PROSPECT: 0,
      TARGET: 0,
      LEAD: 0,
      GOOGLE_TARGET: 0,
    };

    mergedLocations.forEach(loc => {
      const status = loc.shipStatus?.toUpperCase?.() || '';
      if (status === 'ACTIVE') counts.ACTIVE++;
      else if (status === 'ACTIVEOLDER') counts.ACTIVEOLDER++;
      else if (status === 'PROSPECT') counts.PROSPECT++;
      else if (status === 'TARGET') {
        counts.TARGET++;
        if (loc.scope === 'SALESFORCE') counts.LEAD++;
        else {
          counts.GOOGLE_TARGET++;
          console.log('[GOOGLE TARGET]', loc.name, loc.address, loc);
        }
      }
    });

    console.log("Breakdown:");
    console.log("ACTIVE:", counts.ACTIVE);
    console.log("ACTIVEOLDER:", counts.ACTIVEOLDER);
    console.log("PROSPECT:", counts.PROSPECT);
    console.log("TARGET:", counts.TARGET);
    console.log("  - of which LEAD:", counts.LEAD);
    console.log("  - of which GOOGLE TARGET:", counts.GOOGLE_TARGET);


    cachedLocations = mergedLocations;

    console.log("polygons: ", mainData.data?.neighborhoods)
    return {
      data: {
        locations: mergedLocations,
        metadata: {
          origin: mainData.data?.metadata?.origin,
          searchRadius: mainData.data?.metadata?.searchRadius || 0,
          searchCenter: mainData.data?.metadata?.searchCenter || '',
          totalResults:
            (mainData.data?.metadata?.totalResults || 0) +
            (leadsData.data?.metaData?.totalResults || 0),
        },
        neighborhoodPolygons: mainData.data?.neighborhoods
      },
      success: true,
      error: '',
      timeStamp: new Date().toISOString()
    };


  } catch (error) {
    return {
      success: false,
      data: {
        locations: [],
        metadata: {
          totalResults: 0,
          searchRadius: 2,
          searchCenter: ""
        }
      },
      error: error.response?.data?.error || error.message,
      timeStamp: new Date().toISOString()
    };
  }
}


const getLocationId = (location) => {
  if (!location) return null;

  // Check each field in priority order, ensuring it's a valid non-N/A string
  const id = (location?.id && location.id.trim() !== 'N/A' && location.id.trim()) ||
    (location?.sfId && location.sfId.trim() !== 'N/A' && location.sfId.trim()) ||
    (location?.accountId && location.accountId.trim() !== 'N/A' && location.accountId.trim()) ||
    (location?.accountDetails?.accountId && location.accountDetails.accountId.trim() !== 'N/A' && location.accountDetails.accountId.trim()) ||
    (location?.placeId && location.placeId.trim() !== 'N/A' && location.placeId.trim()) ||
    (location?.name) || null;

  if (!id) return null;

  // Determine the type based on which field matched
  let type = 'id';
  if (id === location?.sfId?.trim()) type = 'sfid';
  else if (id === location?.accountId?.trim()) type = 'accountId';
  else if (id === location?.accountDetails?.accountId?.trim()) type = 'acctId';
  else if (id === location?.placeId?.trim()) type = 'placeId';
  else if (id === location?.name) type = 'name';

  return { id, type };
};

export async function getLocationById(value, searchType = 'accountId', userLatLng = null) {
  console.log("Search Location by ID with searchtype:", searchType);

  const normalizedSearchType = ['acctid', 'id', 'sfid'].includes((searchType || '').toLowerCase())
    ? 'accountId'
    : (searchType || '');

  // Only save search if searchType is accountName
  if ((searchType || '').toLowerCase() === 'accountname') {
    saveSearch(searchType, value);
  }

  let response;
  try {
    // ----- 1. Cached Lead shortcut -----------------
    if (normalizedSearchType === 'accountId' && cachedLocations.length > 0) {
      const leadMatch = cachedLocations.find(
        loc => (loc.id === value) && (loc.shipStatus === 'target') && (loc.scope === 'SALESFORCE')
      );
      if (leadMatch) {
        console.log('Returning cached lead result:', leadMatch);
        response = {
          success: true,
          data: { locations: [leadMatch], metadata: { totalResults: 1 } },
          error: '',
          timeStamp: new Date().toISOString(),
        };
      }
    }

    // ----- 2. API call if no cached lead ----------
    if (!response) {
      const api = await apiPromise;

      // sanitize apostrophes like before
      let sanitizedSearchTerm = value;
      if (typeof value === 'string' && value.includes("'")) {
        const parts = value.split(/[\s']+/).filter(Boolean);
        sanitizedSearchTerm = parts.join("%");
        console.log("Sanitized search term:", sanitizedSearchTerm);
      }

      const isAccountName = normalizedSearchType.toLowerCase() === 'accountname';

      // support {lat,lng} or {latitude,longitude} for location param
      const lat = userLatLng?.lat ?? userLatLng?.latitude;
      const lng = userLatLng?.lng ?? userLatLng?.longitude;
      const locationParam = (lat != null && lng != null) ? `${lat},${lng}` : undefined;

      const DEFAULT_RADIUS_MI = 1000;

      const endpoint = isAccountName ? '/v1/locations/name' : '/v1/locations/account';
      const params = isAccountName
        ? {
          accountName: sanitizedSearchTerm,
          ...(locationParam ? { location: locationParam } : {}),
          radius: DEFAULT_RADIUS_MI,
        }
        : { [normalizedSearchType]: sanitizedSearchTerm };

      const res = await api.get(endpoint, { params });
      console.log('Search results (API):', res.data);
      response = res.data;
      // response.data.locations = displaceDuplicates(response.data.locations);

      // 🔹 Add businessUnit = 'US Broadline' for non-targets on accountName search
      // if (isAccountName && Array.isArray(response?.data?.locations)) {
      //   response.data.locations.forEach(loc => {
      //     const isTarget = (loc?.shipStatus ?? loc?.status ?? '').toString().toLowerCase() === 'target';
      //     if (!isTarget) {
      //       loc.businessUnit = 'US Broadline';
      //     }
      //   });
      //   // response.data.locations = dedupeMarkers(response.data.locations, { debug: true }); 
      //   // console.log("After dedupeMarkers: ",response.data.locations);
      // }

      // If the name search returned exactly one match, refetch the full account by id/placeId


      if (isAccountName) {
        const one = response?.data?.locations;
        if (Array.isArray(one) && one.length === 1) {
          const first = one[0];

          const isGoogle = (first?.scope ?? '').toUpperCase() === 'GOOGLE';
          const isLead = (first?.scope ?? '').toUpperCase() === 'SALESFORCE' && (first?.shipStatus ?? '').toUpperCase() === 'TARGET';
          const nonlead = (first?.scope ?? '').toUpperCase() === 'SALESFORCE' && (first?.shipStatus ?? '').toUpperCase() !== 'TARGET';

          const leadId = isLead ? (first?.id ?? first?.sfId ?? null) : null;
          const accountId = nonlead
            ? (getLocationId(first)?.id ?? first?.id ?? first?.sfId)
            : null;

          const placeId = isGoogle
            ? (first?.placeId ?? getLocationId(first)?.id ?? first?.id)
            : null;


          const params = leadId != null
            ? { leadId }
            : accountId != null
              ? { accountId }
              : { placeId }; // by this point one of them is non-null

          console.log(
            'Refetching full account with',
            leadId != null ? `leadId=${leadId}` :
              accountId != null ? `accountId=${accountId}` :
                `placeId=${placeId}`
          );

          if (leadId || accountId || placeId) {
            try {
              const resFull = await api.get('/v1/locations/account', {
                params: params
              });
              if (resFull?.data?.data?.locations?.length) {
                response = resFull.data;
                console.log('Replaced response with full account payload.');
              } else {
                console.warn('Full account fetch returned no locations; keeping name search result.');
              }
            } catch (e) {
              console.warn('Follow-up full account fetch failed; using name search result.', e?.message || e);
            }
          }
        }
      }

      // sync userAccount flag if cached (applies to the final response)
      const firstLoc = response?.data?.locations?.[0];
      if (firstLoc) {
        const cached = cachedLocations.find(r => r.sfId === firstLoc.id);
        if (cached) {
          firstLoc.userAccount = !!cached.userAccount;
        }
      }
    }

    console.log("to save 0 ");

    // ----- 3. Save to recents (only if 1 result) -----
    const locs = response?.data?.locations || [];
    console.log("to save ", locs[0]);

    if (locs.length === 1) {
      const loca = locs[0];
      console.log("to save ", loca);

      const isTarget = (l) => ((l?.shipStatus ?? l?.status ?? '').toString().toLowerCase() === 'target');
      const isSalesforceLead = (l) => isTarget(l) && String(l?.scope ?? '').trim().toUpperCase() === 'SALESFORCE';
      const accountType = isSalesforceLead(loca)
        ? 'TARGET SALESFORCE'
        : (isTarget(loca) ? 'GOOGLE' : (loca?.scope ?? ''));

      console.log("accountType : ", accountType);

      saveRecent({
        accountId: getLocationId(loca)?.id,
        accountType: accountType,
        accountName: loca?.name,
        accountStatus: loca?.shipStatus,
        latitude: loca?.lat,
        longitude: loca?.lng,
      });
    }

    return response;
  } catch (error) {
    return {
      success: false,
      data: { locations: [], metadata: { totalResults: 0 } },
      error: error?.response?.data?.error || error?.message,
      timeStamp: new Date().toISOString(),
    };
  }
}


// GET /api/v1/user  → returns { filters: {...}, favorites: [...] }
export async function getUserSaveData() {
  try {
    console.log("entered to fetch user pref")
    const api = await apiPromise;
    const res = await api.get('/v1/user');
    return { success: true, data: res.data?.data || {} };
  } catch (err) {
    if (err?.response?.status === 404) {
      // user has no prefs yet ― treat as empty success
      return { success: true, data: {} };
    }
    return { success: false, error: err.message || 'getUserSaveData failed' };
  }
}


// POST /api/v1/user/filters?...   — saves defaults
export async function saveFilters(queryString) {
  try {
    const api = await apiPromise;
    await api.post(`/v1/user/filters?${queryString}`);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message || 'saveFilters failed' };
  }
}

// POST /api/v1/user/recents?... — save a recently viewed account
export async function saveRecent({ accountId, accountName, accountType, accountStatus, latitude, longitude }) {
  console.log("update recently account", accountId, accountName, accountStatus, latitude, longitude)
  try {
    const api = await apiPromise;
    const query = new URLSearchParams({
      accountId,
      accountName,
      accountStatus,
      accountType,
      latitude: String(latitude),
      longitude: String(longitude),
    }).toString();
    const res = await api.post(`/v1/user/recents?${query}`);
    return { success: true, data: res.data?.data || [] };
  } catch (err) {
    console.error('[saveRecent] failed', err);
    return { success: false, error: err.message || 'saveRecent failed' };
  }
}

// POST /api/v1/user/search?searchType=...&searchValue=...
export async function saveSearch(searchType, searchValue) {
  try {
    const api = await apiPromise;
    await api.post(`/v1/user/search?searchType=${encodeURIComponent(searchType)}&searchValue=${encodeURIComponent(searchValue)}`);
    console.log('[saveSearch] posted', searchType, searchValue);
    return { success: true };
  } catch (err) {
    console.warn('[saveSearch] failed', err.message);
    return { success: false, error: err.message || 'saveSearch failed' };
  }
}


// --- FAVORITES -------------------------------------------------
export async function saveFavorite({ accountId, accountType, accountName, accountStatus, latitude, longitude }) {
  try {
    const api = await apiPromise;
    await api.post('/v1/user/favorites', null, {
      params: { accountId, accountType, accountName, accountStatus, latitude, longitude }
    });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message || 'saveFavorite failed' };
  }
}

export async function deleteFavorite(accountId) {
  try {
    const api = await apiPromise;
    await api.delete('/v1/user/favorites', { params: { accountId } });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message || 'deleteFavorite failed' };
  }
}


// --- CREATE LEADS ------------------------------------
export async function createLead(payload) {
  console.log('[lead][api] submit start');
  console.log('[lead][api] payload:', payload);
  // const payload_test = {
  //   "Company": "Cuelito",
  //   "FirstName": "Johnathan",
  //   "LastName": "Joestar",
  //   "Status": "Open",
  //   "Potential_Average_Weekly_Sales__c": 100.00,
  //   "Email": "Johnathan.Joestar.@test.com",
  //   "Phone": "1234567890",
  //   "CurrencyIsoCode": "USD",
  //   "Street": "301 East Dr",
  //   "City": "irving",
  //   "State": "TX",
  //   "PostalCode": "75039",
  //   "Country": "USA",
  //   "RecordTypeId": "012E0000000V4MQIA0",
  //   "Category__c": "TOP COMPETITIVE",
  //   "Assign_to_DSM__c": "005E0000000fpiCIAQ",
  //   "Expected_Account_Type__c": "TRS",
  //   "Business_Structure__c": "Local"
  // }
  try {
    const api = await apiPromise;
    // const res = { success: true, error: "err.message || 'createLead failed'" }
    const res = await api.post('/v1/locations/leads', payload);
    console.log('[lead][api] submit complete:', res.data);
    return { success: true, data: res.data };
  }
  catch (err) {
    console.error('[lead][api] submit failed:', err);
    return { success: false, error: err.message || 'createLead failed' };
  }
}

// Feedback
export async function getFeedback({ id }) {
  try {
    const api = await apiPromise;
 
    // IMPORTANT: match your existing convention (no "/api" prefix here)
    const res = await api.get("/v1/feedback", {
      params: { id },
    });
 
    return res.data; // { success, data: [...], error, timeStamp }
  } catch (err) {
    console.error("[getFeedback] failed", err);
    throw err;
  }
}
 
export async function createFeedback({ id, name, scope, reason, comment }) {
  try {
    const api = await apiPromise;
 
    // doc says "query params" even for POST
    const params = { id, name, scope, reason };
    if (comment != null) params.comment = comment;
 
    const res = await api.post("/v1/feedback", null, { params });
 
    return res.data; // { success, data: [...], error, timeStamp }
  } catch (err) {
    console.error("[createFeedback] failed", err);
    throw err;
  }
}
 